import React from 'react';
import {
  AGENT_TOOL_TAG_TEXT_MAX,
  AI_TAG_CORNERS,
  AI_TAG_LABELS,
  AI_TAG_SIZES,
  AI_TAG_SLIDES,
  AiTag,
  AiTagCorner,
  AiTagLabel,
  AiTagSize,
  AiTagSlides,
} from '../../api/settings';
import { AI_LABEL_ICONS } from '../../remotion/shared/remotion/ai-label-icons';

const LABEL_NAMES: Record<AiTagLabel, string> = {
  'ai-generated': 'AI GENERATED',
  'ai-modified': 'AI MODIFIED',
  ai: 'AI',
  custom: 'Your own text',
};
const SIZE_NAMES: Record<AiTagSize, string> = { small: 'Small', medium: 'Medium', large: 'Large' };
const CORNER_NAMES: Record<AiTagCorner, string> = {
  'top-left': 'Top left',
  'top-right': 'Top right',
  'bottom-left': 'Bottom left',
  'bottom-right': 'Bottom right',
};
const SLIDES_NAMES: Record<AiTagSlides, string> = { first: 'First slide', all: 'All slides' };

/** The renderer's plate heights and margin at a slide 1080 px wide, and the preview box standing for that slide. */
const PLATE_HEIGHT: Record<AiTagSize, number> = { small: 44, medium: 64, large: 88 };
const SLIDE_WIDTH = 1080;
const SLIDE_HEIGHT = 1440;
const SLIDE_MARGIN = 28;
const PREVIEW_WIDTH = 216;
const PREVIEW_HEIGHT = (SLIDE_HEIGHT * PREVIEW_WIDTH) / SLIDE_WIDTH;
const inPreview = (slidePx: number) => (slidePx * PREVIEW_WIDTH) / SLIDE_WIDTH;

const FIELD_LABEL = 'mb-1 block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)]';

interface ChoiceProps<T extends string> {
  id: string;
  label: string;
  value: T;
  choices: readonly T[];
  names: Record<T, string>;
  disabled: boolean;
  onChange: (value: T) => void;
}

const Choice = <T extends string>({ id, label, value, choices, names, disabled, onChange }: ChoiceProps<T>) => (
  <div>
    <label htmlFor={id} className={FIELD_LABEL}>
      {label}
    </label>
    <select
      id={id}
      className="select w-full"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {choices.map((choice) => (
        <option key={choice} value={choice}>
          {names[choice]}
        </option>
      ))}
    </select>
  </div>
);

/** The tag on a slide-shaped box, drawn the way the renderer draws it, at the box's scale. */
const Preview: React.FC<{ tag: AiTag }> = ({ tag }) => {
  const plateHeight = inPreview(PLATE_HEIGHT[tag.size]);
  const margin = inPreview(SLIDE_MARGIN);
  const anchor: React.CSSProperties = {
    ...(tag.corner.includes('top') ? { top: margin } : { bottom: margin }),
    ...(tag.corner.includes('right') ? { right: margin } : { left: margin }),
  };
  const preset = tag.label === 'custom' ? null : AI_LABEL_ICONS[tag.label];
  const words = tag.text.trim();

  return (
    <div
      data-testid="ai-tag-preview"
      role="img"
      aria-label="The tag on a slide"
      className="relative shrink-0 overflow-hidden border border-[var(--border-subtle)] bg-[var(--surface-3)]"
      style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
    >
      {preset ? (
        <svg
          data-testid="ai-tag-plate"
          viewBox={preset.viewBox}
          className="absolute"
          style={{ ...anchor, height: plateHeight, width: plateHeight * preset.aspectRatio }}
        >
          <path d={preset.plate} fill="#ffffff" fillRule="evenodd" opacity={0.5} />
          {preset.glyphs.map((d, index) => (
            <path key={index} d={d} fill="#1d1d1b" />
          ))}
        </svg>
      ) : (
        <div
          data-testid="ai-tag-plate"
          className="absolute overflow-hidden text-ellipsis whitespace-nowrap font-bold"
          style={{
            ...anchor,
            height: plateHeight,
            maxWidth: PREVIEW_WIDTH - 2 * margin,
            padding: `0 ${plateHeight / 2}px`,
            borderRadius: plateHeight / 2,
            lineHeight: `${plateHeight}px`,
            fontSize: plateHeight * 0.46,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            color: '#1d1d1b',
            opacity: words ? 1 : 0.6,
          }}
        >
          {words || 'Your text'}
        </div>
      )}
    </div>
  );
};

/**
 * The look of the carousel AI tag: which label, the text of the user's own,
 * size, corner and which slides, with a preview. The server has the last word
 * on the text; this applies only the length. Disabled as a whole while the AI
 * labels switch is off.
 */
const AiTagFields: React.FC<{ value: AiTag; disabled: boolean; onChange: (tag: AiTag) => void }> = ({
  value,
  disabled,
  onChange,
}) => {
  const fieldId = React.useId();
  const set = <K extends keyof AiTag>(key: K) => (next: AiTag[K]) => onChange({ ...value, [key]: next });

  return (
    <div className={`flex flex-col gap-3 ${disabled ? 'opacity-60' : ''}`} data-testid="ai-tag-fields">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="grid min-w-0 flex-1 grid-cols-2 content-start gap-3">
          <Choice
            id={`${fieldId}-label`}
            label="Label"
            value={value.label}
            choices={AI_TAG_LABELS}
            names={LABEL_NAMES}
            disabled={disabled}
            onChange={set('label')}
          />
          <Choice
            id={`${fieldId}-size`}
            label="Size"
            value={value.size}
            choices={AI_TAG_SIZES}
            names={SIZE_NAMES}
            disabled={disabled}
            onChange={set('size')}
          />
          {value.label === 'custom' && (
            <div className="col-span-2">
              <label htmlFor={`${fieldId}-text`} className={FIELD_LABEL}>
                Your text
              </label>
              <input
                id={`${fieldId}-text`}
                type="text"
                className="input w-full"
                maxLength={AGENT_TOOL_TAG_TEXT_MAX}
                value={value.text}
                disabled={disabled}
                onChange={(event) => set('text')(event.target.value)}
              />
            </div>
          )}
          <Choice
            id={`${fieldId}-corner`}
            label="Corner"
            value={value.corner}
            choices={AI_TAG_CORNERS}
            names={CORNER_NAMES}
            disabled={disabled}
            onChange={set('corner')}
          />
          <Choice
            id={`${fieldId}-slides`}
            label="Slides"
            value={value.slides}
            choices={AI_TAG_SLIDES}
            names={SLIDES_NAMES}
            disabled={disabled}
            onChange={set('slides')}
          />
        </div>
        <Preview tag={value} />
      </div>
      <p className="text-[11px] text-[var(--app-text-muted)]">
        The tag goes on carousels that use AI pictures. Reels keep their own label in the editor. Changes apply to
        carousels made from now on.
      </p>
    </div>
  );
};

export default AiTagFields;
